import { createSlice } from '@reduxjs/toolkit';
import { searchPosts } from '../utils/searchPosts';
import { createPost, votePoll } from './posts-actions';
import { CommentData, PostData, ReplyData } from '../models/data';

type PostState = {
  posts: PostData[];
  flaggedInSession: string[]; // postIds explicitly flag-toggled this session
};

const initialState: PostState = {
  posts: [],
  flaggedInSession: [],
};

const postsSlice = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    loadPosts(state, action) {
      state.posts = action.payload.posts;
    },
    mergePosts(state, action) {
      const newPosts = action.payload.posts;
      const existingIds = new Set(state.posts.map(p => p.id));

      newPosts.forEach((newPost: any) => {
        if (!existingIds.has(newPost.id)) {
          state.posts.push(newPost);
        } else {
          const index = state.posts.findIndex(p => p.id === newPost.id);
          if (index >= 0) {
            // If the flag was explicitly toggled this session, preserve it —
            // the incoming data may be stale (e.g. cached loader reused on back-nav).
            const preserveFlag = state.flaggedInSession.includes(newPost.id);
            state.posts[index] = {
              ...newPost,
              isFlagged: preserveFlag ? state.posts[index].isFlagged : newPost.isFlagged,
            };
          }
        }
      });
    },
    editPost(state, action) {
      const post = searchPosts(state, action.payload.post.id) as
        | PostData
        | undefined;

      if (post) {
        post.content = action.payload.post.content;
        post.lastUpdated = action.payload.post.lastUpdated;
        // Sidecar fields that the incident edit endpoint can change —
        // without these, updates to status / severity / category /
        // location / actionTaken / reportedBy required a page refresh
        // because Redux kept the stale `incident` object.
        if (action.payload.post.incident !== undefined) {
          post.incident = action.payload.post.incident;
        }
        if (action.payload.post.kind !== undefined) {
          post.kind = action.payload.post.kind;
        }
      }
    },
    editComment(state, action) {
      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        comment.content = action.payload.comment.content;
        comment.lastUpdated = action.payload.comment.lastUpdated;
      }
    },
    deletePost(state, action) {
      const postIndex = state.posts.findIndex(
        (post) => post.id === action.payload.post.id
      );

      if (postIndex >= 0) {
        state.posts.splice(postIndex, 1);
      }
    },
    deleteComment(state, action) {
      const post = searchPosts(state, action.payload.post.id) as
        | PostData
        | undefined;

      if (post) {
        const commentIndex = post.comments.findIndex(
          (comment) => comment.id === action.payload.comment.id
        );

        if (commentIndex >= 0) {
          post.comments.splice(commentIndex, 1);
        }
      }
    },
    deleteReply(state, action) {
      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        const replyIndex = comment.replies.findIndex(
          (reply) => reply.id === action.payload.reply.id
        );

        if (replyIndex >= 0) {
          comment.replies.splice(replyIndex, 1);
        }
      }
    },
    likePost(state, action) {
      const post = searchPosts(state, action.payload.post.id) as PostData | undefined;

      if (post) {
        const postLikeIndex = post.likes.findIndex(
          (like) => like.id === action.payload.user.id
        );

        if (postLikeIndex < 0) {
          post.likes.push({
            id: action.payload.user.id,
            name: action.payload.user.name,
          });
        } else {
          post.likes.splice(postLikeIndex, 1);
        }
      }
    },
    setPostReviewStatus(state, action) {
      const { postId, reviewStatus } = action.payload;
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        post.reviewStatus = reviewStatus;
      }
    },
    setPostPinned(state, action) {
      const { postId, pinnedAt, pinnedBy } = action.payload;
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        post.pinnedAt = pinnedAt;
        post.pinnedBy = pinnedBy;
      }
    },
    setPostFlagged(state, action) {
      const { postId, isFlagged } = action.payload;
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        post.isFlagged = isFlagged;
        if (!state.flaggedInSession.includes(postId)) {
          state.flaggedInSession.push(postId);
        }
      }
    },
    removePostFile(state, action) {
      const { postId, type, url } = action.payload;
      const post = state.posts.find(p => p.id === postId);
      if (post) {
        if (type === 'image') {
          post.imageUrls = (post.imageUrls || []).filter(u => u !== url);
        } else {
          post.documentUrls = (post.documentUrls || []).filter(u => u !== url);
        }
      }
    },
    updatePostFromBackend(state, action) {
      const postIndex = state.posts.findIndex(
        (post) => post.id === action.payload.post.id
      );

      if (postIndex >= 0) {
        // Update the post with backend data, preserving the structure
        state.posts[postIndex] = {
          ...state.posts[postIndex],
          ...action.payload.post,
        };
      }
    },
    updateCommentFromBackend(state, action) {
      const post = searchPosts(state, action.payload.post.id) as PostData | undefined;

      if (post) {
        const commentIndex = post.comments.findIndex(
          (comment) => comment.id === action.payload.comment.id
        );

        if (commentIndex >= 0) {
          // Update comment with backend data (especially likes array)
          post.comments[commentIndex] = {
            ...post.comments[commentIndex],
            ...action.payload.comment,
            likes: action.payload.comment.likes || [],
          };
        }
      }
    },
    updateReplyFromBackend(state, action) {
      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        // Helper function to find and update reply recursively
        const findAndUpdateReply = (replies: ReplyData[], replyId: string, updatedData: any): boolean => {
          for (let i = 0; i < replies.length; i++) {
            if (replies[i].id === replyId) {
              replies[i] = {
                ...replies[i],
                ...updatedData,
                likes: updatedData.likes || [],
              };
              return true;
            }
            if (replies[i].replies && replies[i].replies.length > 0) {
              if (findAndUpdateReply(replies[i].replies, replyId, updatedData)) {
                return true;
              }
            }
          }
          return false;
        };

        findAndUpdateReply(comment.replies, action.payload.reply.id, action.payload.reply);
      }
    },
    commentPost(state, action) {
      const post = searchPosts(state, action.payload.post.id) as PostData | undefined;

      if (post) {
        const comment = action.payload.comment;
        post.comments.push({
          ...comment,
          // Use backend data if available, otherwise use defaults
          likes: comment.likes || [],
          replies: comment.replies || [],
          created: comment.created_at || comment.created || new Date().toISOString(),
        });
      }
    },
    likeComment(state, action) {
      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        const commentLikeIndex = comment.likes.findIndex(
          (comment) => comment.id === action.payload.user.id
        );

        if (commentLikeIndex < 0) {
          comment.likes.push({
            id: action.payload.user.id,
            name: action.payload.user.name,
          });
        } else {
          comment.likes.splice(commentLikeIndex, 1);
        }
      }
    },
    replyComment(state, action) {
      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        const reply = action.payload.reply;
        comment.replies.push({
          ...reply,
          likes: reply.likes || [],
          created: reply.created_at || reply.created || new Date().toISOString(),
        });
      }
    },
    likeReply(state, action) {
      const reply = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id,
        action.payload.reply.id
      ) as ReplyData | undefined;

      if (reply) {
        const replyLikeIndex = reply.likes.findIndex(
          (like) => like.id === action.payload.user.id
        );

        if (replyLikeIndex < 0) {
          reply.likes.push({
            id: action.payload.user.id,
            name: action.payload.user.name,
          });
        } else {
          reply.likes.splice(replyLikeIndex, 1);
        }
      }
    },
    replyToReply(state, action) {
      // Helper function to find and add reply recursively
      const findAndAddReply = (replies: ReplyData[], parentId: string, newReply: ReplyData): boolean => {
        for (const reply of replies) {
          if (reply.id === parentId) {
            if (!reply.replies) {
              reply.replies = [];
            }
            reply.replies.push({
              ...newReply,
              likes: newReply.likes || [],
              replies: newReply.replies || [],
              created: newReply.created_at || newReply.created || new Date().toISOString(),
            });
            return true;
          }
          if (reply.replies && reply.replies.length > 0) {
            if (findAndAddReply(reply.replies, parentId, newReply)) {
              return true;
            }
          }
        }
        return false;
      };

      const comment = searchPosts(
        state,
        action.payload.post.id,
        action.payload.comment.id
      ) as CommentData | undefined;

      if (comment) {
        findAndAddReply(comment.replies, action.payload.parentReply.id, action.payload.reply);
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(createPost.fulfilled, (state, action) => {
      if (typeof action.payload === 'object') {
        const newPost = action.payload as PostData;
        state.posts.unshift({ ...newPost });
      }
    });
    builder.addCase(votePoll.fulfilled, (state, action) => {
      const updatedPost = action.payload as PostData;
      const index = state.posts.findIndex(p => p.id === updatedPost.id);
      if (index >= 0) {
        state.posts[index] = { ...state.posts[index], poll: updatedPost.poll };
      }
    });
  },
});

export const postsActions = postsSlice.actions;

export default postsSlice.reducer;
